import React, { useState } from "react";

type TrainingSuggestionCardProps<T> = {
  label: string;
  items: T[];
  renderItem: (item: T, isSelected: boolean) => React.ReactNode;
  onAcceptSelected: (items: T[]) => void;
  onReject: () => void;
};

function TrainingSuggestionCard<T>({
  label,
  items,
  renderItem,
  onAcceptSelected,
  onReject,
}: TrainingSuggestionCardProps<T>) {
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const toggle = (i: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  const applySelected = () => {
    onAcceptSelected(items.filter((_, i) => selected.has(i)));
    setSelected(new Set());
  };

  return (
    <div className="mt-2 p-4 rounded-lg bg-purple-500/5 border border-purple-500/30">
      <div className="mb-2 text-xs uppercase tracking-wide text-purple-300">✨ {label}</div>
      <div className="flex flex-wrap gap-2 mb-3">
        {items.map((item, i) => (
          <button
            key={i}
            type="button"
            onClick={() => toggle(i)}
            className={`rounded-md border-2 transition-colors ${
              selected.has(i) ? "border-purple-400" : "border-transparent hover:border-purple-400/40"
            }`}
          >
            {renderItem(item, selected.has(i))}
          </button>
        ))}
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onAcceptSelected(items)}
          className="bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold px-4 py-2 rounded-lg"
        >
          Add all
        </button>
        <button
          type="button"
          onClick={applySelected}
          disabled={selected.size === 0}
          className="bg-gray-700 hover:bg-gray-600 text-gray-200 text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-50"
        >
          Apply selected
        </button>
        <button
          type="button"
          onClick={onReject}
          className="text-gray-400 hover:text-gray-200 text-sm font-semibold px-4 py-2"
        >
          Reject
        </button>
      </div>
    </div>
  );
}

export default TrainingSuggestionCard;
